import React, { useState, FormEvent, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Login.css';

type TabType = 'login' | 'registration';
type RegistrationStep = 'form' | 'verify';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, requestRegistrationCode, verifyRegistrationCode, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('login');
  const [registrationStep, setRegistrationStep] = useState<RegistrationStep>('form');
  const [verificationId, setVerificationId] = useState<number | null>(null);
  const [verificationExpiresAt, setVerificationExpiresAt] = useState<string | null>(null);
  const [code, setCode] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
  const [rememberMe, setRememberMe] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  // Редирект если уже авторизован
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (activeTab === 'login') {
        await login(email, password);
        navigate('/dashboard');
      } else {
        if (registrationStep === 'form') {
          if (password !== passwordConfirmation) {
            setError('Пароли не совпадают');
            setLoading(false);
            return;
          }
          const res = await requestRegistrationCode(name, email, password, passwordConfirmation);
          setVerificationId(res.verification_id);
          setVerificationExpiresAt(res.expires_at);
          setRegistrationStep('verify');
        } else {
          if (!verificationId) {
            setError('Не найден запрос на подтверждение. Начните регистрацию заново.');
            setRegistrationStep('form');
            setLoading(false);
            return;
          }
          await verifyRegistrationCode(verificationId, code);
          navigate('/dashboard');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Произошла ошибка. Попробуйте еще раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setName(e.target.value);
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setPassword(e.target.value);
  };

  const handlePasswordConfirmationChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setPasswordConfirmation(e.target.value);
  };

  const handleRememberMeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setRememberMe(e.target.checked);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setError('');
    setEmail('');
    setName('');
    setPassword('');
    setPasswordConfirmation('');
    setCode('');
    setVerificationId(null);
    setVerificationExpiresAt(null);
    setRegistrationStep('form');
  };

  return (
    <div className="login-container">
      <div className="login-modal">
        <div className="login-header">
          <img src="/icons/cam.svg" alt="Logo" className="login-logo" />
        </div>
        
        <h1 className="login-title">Вход в систему</h1>
        
        <div className="login-tabs">
          <button 
            type="button"
            className={`login-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => handleTabChange('login')}
          >
            Вход
          </button>
          <button 
            type="button"
            className={`login-tab ${activeTab === 'registration' ? 'active' : ''}`}
            onClick={() => handleTabChange('registration')}
          >
            Регистрация
          </button>
        </div>

        {error && (
          <div className="login-error" style={{ 
            color: '#ff4444', 
            padding: '10px', 
            marginBottom: '10px',
            textAlign: 'center',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          {activeTab === 'registration' && (
            <>
              {registrationStep === 'form' ? (
                <>
                  <input
                    type="text"
                    className="login-input"
                    placeholder="Имя"
                    value={name}
                    onChange={handleNameChange}
                    required
                  />
                  <input
                    type="email"
                    className="login-input"
                    placeholder="Email"
                    value={email}
                    onChange={handleEmailChange}
                    required
                  />
                  <input
                    type="password"
                    className="login-input"
                    placeholder="Пароль"
                    value={password}
                    onChange={handlePasswordChange}
                    required
                    minLength={8}
                  />
                  <input
                    type="password"
                    className="login-input"
                    placeholder="Подтвердите пароль"
                    value={passwordConfirmation}
                    onChange={handlePasswordConfirmationChange}
                    required
                    minLength={8}
                  />
                </>
              ) : (
                <>
                  <div style={{ fontSize: '13px', color: '#c9c9c9', marginBottom: '10px', textAlign: 'center' }}>
                    Мы отправили код на <b>{email}</b>
                    {verificationExpiresAt ? (
                      <>
                        <br />
                        Действует до: {new Date(verificationExpiresAt).toLocaleString()}
                      </>
                    ) : null}
                  </div>
                  <input
                    type="text"
                    className="login-input"
                    placeholder="Код из письма"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\s+/g, ''))}
                    required
                    inputMode="numeric"
                    maxLength={10}
                  />
                  <button
                    type="button"
                    className="login-button"
                    disabled={loading}
                    onClick={() => {
                      setError('');
                      setCode('');
                      setVerificationId(null);
                      setVerificationExpiresAt(null);
                      setRegistrationStep('form');
                    }}
                    style={{ marginTop: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.25)' }}
                  >
                    Назад
                  </button>
                </>
              )}
            </>
          )}
          {activeTab === 'login' && (
            <>
              <input
                type="email"
                className="login-input"
                placeholder="Email"
                value={email}
                onChange={handleEmailChange}
                required
              />
              <input
                type="password"
                className="login-input"
                placeholder="Пароль"
                value={password}
                onChange={handlePasswordChange}
                required
                minLength={8}
              />
            </>
          )}
          
          <button type="submit" className="login-button" disabled={loading}>
            {loading
              ? 'Загрузка...'
              : activeTab === 'login'
                ? 'Войти'
                : registrationStep === 'form'
                  ? 'Отправить код'
                  : 'Подтвердить и войти'}
          </button>

          <div className="login-options">
            <label className="login-checkbox-label">
              <input
                type="checkbox"
                className="login-checkbox"
                checked={rememberMe}
                onChange={handleRememberMeChange}
              />
              <span>Запомнить меня</span>
            </label>
            <a href="#" className="login-forgot">Забыли пароль?</a>
          </div>

          {/* <div className="login-divider">
            <span className="login-divider-line"></span>
            <span className="login-divider-text">или</span>
            <span className="login-divider-line"></span>
          </div>

          <div className="login-social">
            <button type="button" className="login-social-button">
              <img src="/logo/google.svg" alt="Google" className="login-social-icon" />
              <span>Google</span>
            </button>
            <button type="button" className="login-social-button">
              <img src="/logo/apple.svg" alt="Apple" className="login-social-icon" />
              <span>Apple</span>
            </button>
          </div> */}
        </form>
      </div>
    </div>
  );
};

export default Login;
